import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import { CalendarFilters, DEFAULT_FILTERS, FilterStats, AdvancedFilters, DEFAULT_ADVANCED_FILTERS, AdvancedFilterCondition } from '@/types/filters';
import { Resource, Booking, AppSettings } from '@/types';
import { calculatePeriodUtilization } from '@/utils/heatmap-utils';

interface FilterContextType {
  filters: CalendarFilters;
  setFilters: (filters: CalendarFilters) => void;
  updateFilter: <K extends keyof CalendarFilters>(key: K, value: CalendarFilters[K]) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
  filterStats: FilterStats | null;
  setFilterStats: (stats: FilterStats | null) => void;
  applyFilters: (resources: Resource[], bookings: Booking[], settings?: AppSettings, projectDetails?: any[], costCentres?: any[], dateRange?: { startDate: Date; endDate: Date }) => {
    filteredResources: Resource[];
    filteredBookings: Booking[];
    stats: FilterStats;
  };
  advancedFilters: AdvancedFilters;
  setAdvancedFilters: (filters: AdvancedFilters) => void;
  hasActiveAdvancedFilters: boolean;
  resetAdvancedFilters: () => void;
  showFilteredBookings: boolean;
  setShowFilteredBookings: (show: boolean) => void;
  hasHiddenBookings: boolean;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

// Helper function to evaluate a single advanced filter condition
function evaluateCondition(booking: Booking, condition: AdvancedFilterCondition): boolean {
  const fieldValue = getFieldValue(booking, condition.fieldName);
  const { operator, value, value2 } = condition;

  // Handle null/undefined values
  if (operator === 'is-empty') {
    return fieldValue === null || fieldValue === undefined || fieldValue === '';
  }
  if (operator === 'is-not-empty') {
    return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
  }

  // For other operators, if field value is null/undefined, return false
  if (fieldValue === null || fieldValue === undefined) {
    return false;
  }

  const fvStr = String(fieldValue).toLowerCase();
  const vStr = String(value).toLowerCase();

  switch (operator) {
    case 'equals':
      return fvStr === vStr;
    case 'not-equals':
      return fvStr !== vStr;
    case 'contains':
      return fvStr.includes(vStr);
    case 'not-contains':
      return !fvStr.includes(vStr);
    case 'starts-with':
      return fvStr.startsWith(vStr);
    case 'ends-with':
      return fvStr.endsWith(vStr);
    case 'greater-than': {
      const fvDate = new Date(String(fieldValue));
      const vDate = new Date(String(value));
      if (!isNaN(fvDate.getTime()) && !isNaN(vDate.getTime())) return fvDate > vDate;
      return Number(fieldValue) > Number(value);
    }
    case 'less-than': {
      const fvDate = new Date(String(fieldValue));
      const vDate = new Date(String(value));
      if (!isNaN(fvDate.getTime()) && !isNaN(vDate.getTime())) return fvDate < vDate;
      return Number(fieldValue) < Number(value);
    }
    case 'between': {
      const fvDate = new Date(String(fieldValue));
      const vDateFrom = new Date(String(value));
      const vDateTo = new Date(String(value2));
      if (!isNaN(fvDate.getTime()) && !isNaN(vDateFrom.getTime()) && !isNaN(vDateTo.getTime())) return fvDate >= vDateFrom && fvDate <= vDateTo;
      return Number(fieldValue) >= Number(value) && Number(fieldValue) <= Number(value2);
    }
    case 'in':
      return Array.isArray(value) && value.some(v => fvStr === String(v).toLowerCase());
    case 'not-in':
      return Array.isArray(value) && !value.some(v => fvStr === String(v).toLowerCase());
    default:
      return true;
  }
}

// Helper function to get field value from booking object
function getFieldValue(booking: Booking, fieldName: string): any {
  // Handle nested fields (e.g., "userChar.1", "userNumber.2")
  if (fieldName.includes('.')) {
    const [parent, key] = fieldName.split('.');
    const parentObj = (booking as any)[parent];
    return parentObj ? parentObj[key] : undefined;
  }
  return (booking as any)[fieldName];
}

export function FilterProvider({ children }: { children: ReactNode }) {
  const [filters, setFilters] = useState<CalendarFilters>(DEFAULT_FILTERS);
  const [filterStats, setFilterStats] = useState<FilterStats | null>(null);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(DEFAULT_ADVANCED_FILTERS);
  const [showFilteredBookings, setShowFilteredBookings] = useState(false);
  const [hasHiddenBookings, setHasHiddenBookings] = useState(false);

  const updateFilter = <K extends keyof CalendarFilters>(
    key: K,
    value: CalendarFilters[K]
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setFilterStats(null);
    setShowFilteredBookings(false);
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters(DEFAULT_ADVANCED_FILTERS);
    setShowFilteredBookings(false);
  };

  const hasActiveFilters = useMemo(() => {
    return (
      filters.departments.length > 0 ||
      filters.resources.length > 0 ||
      filters.roles.length > 0 ||
      (filters.utilisationRange[0] !== 0 || filters.utilisationRange[1] !== 100) ||
      filters.overUtilisedOnly ||
      filters.projects.length > 0 ||
      filters.projectStatuses.length > 0 ||
      filters.costCentres.length > 0 ||
      filters.costCentreStatuses.length > 0 ||
      filters.bookingTypes.length > 0 ||
      filters.bookingStatuses.length > 0
    );
  }, [filters]);

  const hasActiveAdvancedFilters = useMemo(() => {
    return advancedFilters.conditions.length > 0;
  }, [advancedFilters]);

  const applyFilters = React.useCallback((resources: Resource[], bookings: Booking[], settings?: AppSettings, projectDetails?: any[], costCentres?: any[], dateRange?: { startDate: Date; endDate: Date }) => {
    let filteredResources = [...resources];
    let filteredBookings = [...bookings];
    let bookingsHidden = false;

    // Hide inactive resources from the calendar (they still appear in the Resources tab)
    filteredResources = filteredResources.filter((resource) =>
      resource.status !== 'inactive' && resource.status !== 'off'
    );

    // Bidirectional filtering logic:
    // 1. If only roles are selected -> filter resources by role, then departments by those resources
    // 2. If only departments are selected -> filter resources by department, then roles by those resources
    // 3. If only resources are selected -> filter departments and roles by those resources
    // 4. If multiple filters are selected -> apply them progressively (departments -> roles -> resources)

    const hasOnlyRoles = filters.roles.length > 0 && filters.departments.length === 0 && filters.resources.length === 0;
    const hasOnlyDepartments = filters.departments.length > 0 && filters.roles.length === 0 && filters.resources.length === 0;
    const hasOnlyResources = filters.resources.length > 0 && filters.departments.length === 0 && filters.roles.length === 0;

    if (hasOnlyRoles) {
      // When only roles are selected: filter resources by role
      filteredResources = filteredResources.filter((resource) =>
        filters.roles.includes(resource.role)
      );
    } else if (hasOnlyDepartments) {
      // When only departments are selected: filter resources by department
      const groupField = settings?.groupingMethod === 'scheduler-group' ? 'schedulerGroup' : 'department';
      filteredResources = filteredResources.filter((resource) => {
        const resourceValue = resource[groupField as keyof Resource] as string | undefined;
        return resourceValue && filters.departments.includes(resourceValue);
      });
    } else if (hasOnlyResources) {
      // When only resources are selected: filter by specific resource IDs
      filteredResources = filteredResources.filter((resource) =>
        filters.resources.includes(resource.id)
      );
    } else {
      // Multiple filters selected: apply progressively
      
      // Step 1: Apply Department/Group Filter
      if (filters.departments.length > 0) {
        const groupField = settings?.groupingMethod === 'scheduler-group' ? 'schedulerGroup' : 'department';
        filteredResources = filteredResources.filter((resource) => {
          const resourceValue = resource[groupField as keyof Resource] as string | undefined;
          return resourceValue && filters.departments.includes(resourceValue);
        });
      }

      // Step 2: Apply Roles Filter (on already filtered resources)
      if (filters.roles.length > 0) {
        filteredResources = filteredResources.filter((resource) =>
          filters.roles.includes(resource.role)
        );
      }

      // Step 3: Apply Resources Filter (on already filtered resources)
      if (filters.resources.length > 0) {
        filteredResources = filteredResources.filter((resource) =>
          filters.resources.includes(resource.id)
        );
      }
    }

    // Filter bookings to only include those for filtered resources
    const filteredResourceIds = new Set(filteredResources.map((r) => r.id));
    filteredBookings = filteredBookings.filter((booking) =>
      booking.resourceId === 'unallocated' || (booking.resourceId != null && filteredResourceIds.has(booking.resourceId))
    );

    // Apply Utilisation Filter (filter resources based on their booking utilisation)
    if (filters.utilisationRange[0] !== 0 || filters.utilisationRange[1] !== 100 || filters.overUtilisedOnly) {
      const resourceUtilisation = new Map<string, number>();
      
      if (dateRange) {
        const globalWorkingProfile = settings?.globalWorkingProfile || { monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8, saturday: 0, sunday: 0 };
        filteredResources.forEach(resource => {
          const { utilizationPercentage } = calculatePeriodUtilization(
            String(resource.id),
            dateRange.startDate,
            dateRange.endDate,
            bookings,
            resource,
            globalWorkingProfile
          );
          resourceUtilisation.set(resource.id, utilizationPercentage);
        });
      } else {
        filteredResources.forEach(resource => {
          resourceUtilisation.set(resource.id, 0);
        });
      }
      
      // Filter resources by utilisation range
      const beforeCount = filteredResources.length;
      filteredResources = filteredResources.filter(resource => {
        const utilisation = resourceUtilisation.get(resource.id) || 0;
        
        // If "Over Utilised" toggle is ON, override slider and show only resources >= 100.1%
        if (filters.overUtilisedOnly) {
          return utilisation >= 100.1;
        }
        
        // Otherwise, use the slider range
        const inRange = utilisation >= filters.utilisationRange[0] && utilisation <= filters.utilisationRange[1];
        return inRange;
      });
      
      // Update filtered bookings to match filtered resources
      const updatedResourceIds = new Set(filteredResources.map(r => r.id));
      filteredBookings = filteredBookings.filter(booking => booking.resourceId != null && updatedResourceIds.has(booking.resourceId));
      
      // Track if bookings were hidden
      if (beforeCount > filteredResources.length) {
        bookingsHidden = true;
      }
    }

    // Apply booking-attribute filters (Project, Cost Centre, Booking Type) with AND logic across types
    // When multiple filter types are active, a booking must match ALL active filter types
    // Within each type, values are OR (e.g. Project A or Project B)
    const hasProjectFilter = filters.projects.length > 0;
    const hasProjectStatusFilter = filters.projectStatuses.length > 0 && projectDetails;
    const hasCostCentreFilter = filters.costCentres.length > 0;
    const hasCostCentreStatusFilter = filters.costCentreStatuses.length > 0 && costCentres;
    const hasBookingTypeFilter = filters.bookingTypes.length > 0;
    const hasBookingStatusFilter = filters.bookingStatuses.length > 0;

    const matchesProjectFilter = (booking: Booking): boolean => {
      const hasNoProjectFilter = filters.projects.includes('no-project');
      const bookingHasNoProject = !booking.projectId || booking.projectId === '';
      if (hasNoProjectFilter && bookingHasNoProject) return true;
      const selectedProjectIds = filters.projects.filter(p => p !== 'no-project');
      return selectedProjectIds.length > 0 && selectedProjectIds.includes(String(booking.projectId));
    };

    const matchesCostCentreFilter = (booking: Booking): boolean => {
      const hasNoCostCentreFilter = filters.costCentres.includes('no-cost-centre');
      const bookingHasNoCostCentre = !booking.costCentreId || booking.costCentreId == null;
      if (hasNoCostCentreFilter && bookingHasNoCostCentre) return true;
      const selectedCostCentreIds = filters.costCentres.filter(cc => cc !== 'no-cost-centre');
      return selectedCostCentreIds.length > 0 && selectedCostCentreIds.includes(String(booking.costCentreId || ''));
    };

    const matchesBookingTypeFilter = (booking: Booking): boolean => {
      const hasNoBookingTypeFilter = filters.bookingTypes.includes('no-booking-type');
      const bookingHasNoBookingType = !booking.bookingTypeId || booking.bookingTypeId === '';
      if (hasNoBookingTypeFilter && bookingHasNoBookingType) return true;
      const selectedBookingTypeIds = filters.bookingTypes.filter(bt => bt !== 'no-booking-type');
      return selectedBookingTypeIds.length > 0 && selectedBookingTypeIds.includes(String(booking.bookingTypeId || ''));
    };

    const activeFilterCount = [hasProjectFilter, hasCostCentreFilter, hasBookingTypeFilter].filter(Boolean).length;

    if (activeFilterCount > 0) {
      filteredBookings = filteredBookings.filter((booking) => {
        const matches: boolean[] = [];
        if (hasProjectFilter) matches.push(matchesProjectFilter(booking));
        if (hasCostCentreFilter) matches.push(matchesCostCentreFilter(booking));
        if (hasBookingTypeFilter) matches.push(matchesBookingTypeFilter(booking));
        return matches.every(m => m);
      });
    }

    // Project Status and Cost Centre Status are always AND (hard filters)
    if (hasProjectStatusFilter) {
      filteredBookings = filteredBookings.filter((booking) => {
        if (!booking.projectId || booking.projectId === '') return false;
        const project = projectDetails?.find(p => String(p.id) === String(booking.projectId));
        return !!project && filters.projectStatuses.includes(project.status);
      });
    }

    if (hasCostCentreStatusFilter) {
      filteredBookings = filteredBookings.filter((booking) => {
        if (!booking.costCentreId) return false;
        const costCentre = costCentres?.find(cc => String(cc.id) === String(booking.costCentreId));
        return !!costCentre && filters.costCentreStatuses.includes(costCentre.status);
      });
    }

    // Apply Booking Status Filter (always AND — status is a hard filter)
    if (hasBookingStatusFilter) {
      filteredBookings = filteredBookings.filter((booking) =>
        filters.bookingStatuses.includes(booking.status)
      );
    }

    // Apply Advanced Filters
    if (advancedFilters.conditions.length > 0) {
      filteredBookings = filteredBookings.filter((booking) => {
        const results = advancedFilters.conditions.map((condition) => 
          evaluateCondition(booking, condition)
        );
        
        // Apply logic operator (AND/OR)
        return advancedFilters.logicOperator === 'AND'
          ? results.every(r => r)
          : results.some(r => r);
      });
    }

    // Track if any bookings were hidden
    bookingsHidden = bookingsHidden || filteredBookings.length < bookings.length;
    setHasHiddenBookings(bookingsHidden);

    // Return filtered data with stats
    return { 
      filteredResources, 
      filteredBookings,
      stats: {
        totalResources: resources.length,
        filteredResources: filteredResources.length,
        totalBookings: bookings.length,
        filteredBookings: filteredBookings.length,
      }
    };
  }, [filters, advancedFilters]);

  return (
    <FilterContext.Provider
      value={{
        filters,
        setFilters,
        updateFilter,
        resetFilters,
        hasActiveFilters,
        filterStats,
        setFilterStats,
        applyFilters,
        advancedFilters,
        setAdvancedFilters,
        hasActiveAdvancedFilters,
        resetAdvancedFilters,
        showFilteredBookings,
        setShowFilteredBookings,
        hasHiddenBookings,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
}

export function useFilters() {
  const context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
}
